import { useRef, type ChangeEvent } from "react";

interface Props {
  value: string;
  /** `file` is the selected File when uploading; omitted when clearing. */
  onChange: (filename: string, dataUrl?: string, file?: File) => void;
  label?: string;
  helper?: string;
  optionalLabel?: string;
  uploadBtnLabel?: string;
  cameraBtnLabel?: string;
  uploadedLabel?: string;
  changeBtnLabel?: string;
  accept?: string;
  /** Passport step: passport icon, camera as primary (green), file secondary, centered actions */
  variant?: "default" | "passportHero";
  /** When provided, the camera control opens this flow instead of a single-shot capture input. */
  onCameraButtonPress?: () => void;
  /** How to remove an uploaded file (non-identity flows often use trash to match organizer document cards). */
  uploadedClearAction?: "changeLink" | "trashIcon";
  removeAriaLabel?: string;
  /** When set with `uploadedClearAction: "trashIcon"`, trash opens parent flow (e.g. confirm modal) instead of clearing immediately. */
  onRequestClear?: () => void;
}

export default function FileUpload({
  value,
  onChange,
  label,
  helper,
  optionalLabel,
  uploadBtnLabel = "Seleccionar archivo",
  cameraBtnLabel = "Usar cámara",
  uploadedLabel = "Archivo adjunto:",
  changeBtnLabel = "Cambiar",
  accept = "image/jpeg,image/png,application/pdf,image/svg+xml",
  variant = "default",
  onCameraButtonPress,
  uploadedClearAction = "changeLink",
  removeAriaLabel = "Remove file",
  onRequestClear,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      onChange(file.name, ev.target?.result as string, file);
    };
    reader.readAsDataURL(file);
  };

  if (value) {
    return (
      <div>
        {label && (
          <div className="flex items-center gap-2 mb-1.5">
            <label className="text-[13px] font-semibold text-slate-500 uppercase tracking-wide">
              {label}
            </label>
            {optionalLabel && (
              <span className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">
                {optionalLabel}
              </span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 shrink-0">
              <i className="ri-file-check-line text-emerald-600 text-base" />
            </div>
            <div className="min-w-0">
              <p className="text-[12px] text-emerald-700 font-medium">{uploadedLabel}</p>
              <p className="text-[13px] text-slate-700 truncate">{value}</p>
            </div>
          </div>
          {uploadedClearAction === "trashIcon" ? (
            <button
              type="button"
              onClick={() => {
                if (onRequestClear) {
                  onRequestClear();
                  return;
                }
                onChange("", "", undefined);
                if (fileRef.current) fileRef.current.value = "";
                if (cameraRef.current) cameraRef.current.value = "";
              }}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer shrink-0 ml-2 transition-colors"
              aria-label={removeAriaLabel}
              title={removeAriaLabel}
            >
              <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                onChange("", "", undefined);
                if (fileRef.current) fileRef.current.value = "";
                if (cameraRef.current) cameraRef.current.value = "";
              }}
              className="text-[12px] text-slate-500 underline cursor-pointer ml-3 shrink-0 whitespace-nowrap"
            >
              {changeBtnLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {label && (
        <div className="flex items-center gap-2 mb-1.5">
          <label className="text-[13px] font-semibold text-slate-500 uppercase tracking-wide">
            {label}
          </label>
          {optionalLabel && (
            <span className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">
              {optionalLabel}
            </span>
          )}
        </div>
      )}
      <div
        className={`border-2 border-dashed border-slate-200 rounded-xl bg-white ${
          variant === "passportHero" ? "p-6" : "p-5"
        }`}
        role="region"
        aria-label="Subir archivo"
      >
        <div
          className={`flex flex-col items-center text-center ${
            variant === "passportHero" ? "gap-0 mb-3" : "gap-3 mb-4"
          }`}
        >
          <div
            className={`flex items-center justify-center mx-auto ${
              variant === "passportHero" ? "w-12 h-12 mb-2" : "w-12 h-12 rounded-full bg-slate-50"
            }`}
          >
            <i
              className={
                variant === "passportHero"
                  ? "ri-passport-line text-3xl text-slate-300"
                  : "ri-upload-cloud-2-line text-slate-400 text-2xl"
              }
            />
          </div>
          {helper && (
            <p
              className={`text-[13px] text-slate-500 leading-relaxed ${
                variant === "passportHero" ? "mb-0" : ""
              }`}
            >
              {helper}
            </p>
          )}
        </div>
        <div
          className={`flex gap-2 ${
            variant === "passportHero" ? "justify-center flex-wrap" : ""
          }`}
        >
          {variant === "passportHero" ? (
            <>
              <button
                type="button"
                onClick={() =>
                  onCameraButtonPress ? onCameraButtonPress() : cameraRef.current?.click()}
                className="px-4 h-10 flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[13px] font-medium cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className={onCameraButtonPress ? "ri-scan-line text-sm" : "ri-camera-line text-sm"} />
                {cameraBtnLabel}
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="px-4 h-10 flex items-center gap-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-600 rounded-xl text-[13px] font-medium cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-file-upload-line text-sm" />
                {uploadBtnLabel}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex-1 h-11 flex items-center justify-center gap-2 bg-white border border-slate-200 rounded-xl text-[14px] font-medium text-slate-700 cursor-pointer hover:border-emerald-400 hover:text-emerald-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-file-upload-line text-base" />
                {uploadBtnLabel}
              </button>
              <button
                type="button"
                onClick={() =>
                  onCameraButtonPress ? onCameraButtonPress() : cameraRef.current?.click()}
                className="h-11 px-4 flex items-center justify-center gap-2 bg-white border border-slate-200 rounded-xl text-[14px] font-medium text-slate-700 cursor-pointer hover:border-emerald-400 hover:text-emerald-700 transition-colors whitespace-nowrap"
              >
                <i
                  className={onCameraButtonPress ? "ri-scan-line text-base" : "ri-camera-line text-base"}
                />
                {cameraBtnLabel}
              </button>
            </>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          onChange={handleFile}
          className="hidden"
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFile}
          className="hidden"
        />
      </div>
    </div>
  );
}
